import { useEffect, useState } from "react";

import { Modal } from "@newsela/angelou";

import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { SearchTags } from "./SearchTags";
import { TaxonomyModalFooter } from "./TaxonomyModalFooter";
import { TaxonomyModalHeader } from "./TaxonomyModalHeader/TaxonomyModalHeader.component";
import { TaxonomyModalTabs } from "./TaxonomyModalTabs";
import { useTaxonomyTagsContext } from "./TaxonomyTags.provider";
import { modalContainerCss, modalContentCss } from "./TaxonomyTags.styles";
import { IModalProps, ITaxonomyCategorizedData } from "./TaxonomyTags.types";
import { searchTags } from "./utils";
import {
  collectTaxonomySlugs,
  getIdsFromDifferenceInQuery,
  getUniqueElements,
} from "./utils/processCategoryData";

interface TaxonomyTagsProps {
  modalProps: IModalProps;
  setActive: (isActive: boolean) => void;
}

export const TaxonomyTags = ({ modalProps, setActive }: TaxonomyTagsProps) => {
  const {
    handleTopicSubmit,
    selectionState,
    setSelectionState,
    isTabletOrMobileView,
    categorizedData,
    toggleSelection,
    prevTaxonomyQueryValue,
    setPrevTaxonomyQueryValue,
    latestSavedSelectionState,
    setLatestSavedSelectionState,
  } = useTaxonomyTagsContext();

  const [filteredData, setFilteredData] =
    useState<ITaxonomyCategorizedData>(categorizedData);
  const {
    handlers: {
      setTaxonomyTagsSlugs,
      setSearchParamValue,
      deleteSearchParamValues,
    },
  } = useSearchPageContext();

  const handleSearch = (query: string) => {
    const result = searchTags(query, categorizedData);

    setFilteredData(result);
  };

  const handleSubmit = () => {
    const newTaxonomyTags = handleTopicSubmit();
    setLatestSavedSelectionState(selectionState);
    if (newTaxonomyTags) {
      const taxonomyTagsSlugs = collectTaxonomySlugs(newTaxonomyTags);
      if (taxonomyTagsSlugs.length === 0) {
        setSelectionState({});
        setLatestSavedSelectionState({});
        deleteSearchParamValues(AgathaSchemaParamsEnum.taxonomy_tags);
      } else {
        const differenceInQuery = getUniqueElements(
          prevTaxonomyQueryValue,
          taxonomyTagsSlugs,
        );
        const idsToToggle = getIdsFromDifferenceInQuery(
          differenceInQuery,
          categorizedData,
        );

        if (idsToToggle.length > 0) {
          let heighestOrderId;
          Object.keys(categorizedData).forEach((level) => {
            const currentLevelData = categorizedData[level];
            if (currentLevelData && currentLevelData.length > 0) {
              const heighestOrderTag = currentLevelData.filter((tag) => {
                return idsToToggle.includes(tag.id);
              });
              if (heighestOrderTag.length > 0) {
                heighestOrderId = heighestOrderTag[0]?.id;
              }
            }
          });
          if (
            heighestOrderId &&
            prevTaxonomyQueryValue.length > taxonomyTagsSlugs.length
          ) {
            toggleSelection(heighestOrderId, true);
          }
          setTaxonomyTagsSlugs(taxonomyTagsSlugs);
          setSearchParamValue(
            AgathaSchemaParamsEnum.taxonomy_tags,
            taxonomyTagsSlugs.join(","),
          );
        }
        setPrevTaxonomyQueryValue(taxonomyTagsSlugs);
      }
    }
    setActive(false);
  };

  const handleClose = () => {
    if (
      latestSavedSelectionState &&
      Object.keys(latestSavedSelectionState).length > 0
    )
      setSelectionState(latestSavedSelectionState);
    setActive(false);
  };

  useEffect(() => {
    if (!latestSavedSelectionState) {
      setLatestSavedSelectionState(selectionState);
    }
  }, []);

  return (
    <Modal
      {...modalProps}
      onBackgroundClick={handleClose}
      labelId="modal_title"
      ariaProps={{
        "aria-labelledby": "modal_title",
        "aria-describedby": "modal_description",
      }}
      dataProps={{
        "data-testid": "taxonomy-tags-modal",
      }}
      __cssFor={{ root: modalContainerCss }}
    >
      <div css={modalContentCss}>
        <TaxonomyModalHeader setActive={setActive} />
        {isTabletOrMobileView && <SearchTags onSearch={handleSearch} />}
        <TaxonomyModalTabs
          filteredData={filteredData}
          handleSearch={handleSearch}
        />
        <TaxonomyModalFooter onSubmit={handleSubmit} onCancel={handleClose} />
      </div>
    </Modal>
  );
};
